import './style.scss';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import React, { useState } from 'react';
import htmlToDraft from 'html-to-draftjs';
import embed from 'embed-video';
import draftToHtml from 'draftjs-to-html';
import { useMutation } from '@apollo/client';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState, Modifier, RichUtils } from 'draft-js';

import { GQL_UPLOAD_IMAGE } from '../../gqls';
import { REGEX_HTTPS, uploadImageCallback } from '../../utils';

export const getPlainText = value => {
  const text = convertToRaw(value.getCurrentContent())
    .blocks.map(block => (!block.text.trim() && '\n') || block.text)
    .join('\n')
    .trim();

  return text;
};

// html-to-draftjs mis-shifts a leading top-level <img>/<iframe>: the `atomic` type lands on
// block 0 (empty, no entity) while the entity lands on block 1. The editor then renders that
// atomic block, calls getEntityAt(0) -> null, and draft-js throws `Unknown DraftEntity key: null`.
// draftToHtml serialises images as bare top-level <img/>, so saved content reproduces it on reload.
const BLOCK_LEVEL_START = /^\s*<(p|div|h[1-6]|ul|ol|blockquote|pre|table|figure)\b/i;

export const getEditorContent = value => {
  const html = String(value === null || value === undefined ? '' : value);
  // Leading with a block element keeps the atomic block and its entity on the same block.
  const normalized = BLOCK_LEVEL_START.test(html) ? html : `<p></p>${html}`;
  // Backstop for shapes the prefix cannot fix (e.g. <figure><img/></figure>): an atomic block
  // with no entity is the shift artefact, so re-type it and nothing will ask it for an entity.
  const contentBlocks = htmlToDraft(normalized).contentBlocks.map(block =>
    block.getType() === 'atomic' && block.getEntityAt(0) === null ? block.set('type', 'unstyled') : block
  );
  const content = EditorState.createWithContent(ContentState.createFromBlockArray(contentBlocks));

  return content;
};

export const getContentString = value => draftToHtml(convertToRaw(value.getCurrentContent()));

const CustomColorOption = ({ editorState, onChange }) => {
  const [color, setColor] = useState('#000000');

  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor);

    const selection = editorState.getSelection();
    let currentContent = editorState.getCurrentContent();
    const currentStyles = editorState.getCurrentInlineStyle();
    
    // Clear old custom colors applied to the specific selection
    currentStyles.forEach((style) => {
      if (style && style.startsWith('color-')) {
        currentContent = Modifier.removeInlineStyle(currentContent, selection, style);
      }
    });

    let nextEditorState = EditorState.push(editorState, currentContent, 'change-inline-style');
    // Apply the chosen hex natively via standard draft prefixing rules
    nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, `color-${newColor}`);
    onChange(nextEditorState);
  };

  return (
    <div className="rdw-option-wrapper" style={{ padding: '0 4px', display: 'flex', alignItems: 'center' }}>
      <input 
        type="color" 
        value={color} 
        onChange={handleColorChange} 
        className='rdw-option-color-input'
        title="Custom Text Color"
      />
    </div>
  );
};

const RichEditor = ({
  editorState,
  onEditorStateChange,
  invalidState,
  addOptions = [],
  hideOptions = [],
  enableCustomColorPicker = false,
  onBlur = () => {}
}) => {
  const [uploadImage] = useMutation(GQL_UPLOAD_IMAGE);

  const uploadCallback = file => uploadImageCallback(file, uploadImage);
  const options = [
    'inline',
    'blockType',
    'fontSize',
    'fontFamily',
    'list',
    'link',
    'embedded',
    'image',
    ...addOptions
  ].filter(option => hideOptions.findIndex(hideOption => hideOption === option) === -1);

  return (
    <Editor
      onBlur={onBlur}
      editorState={editorState}
      wrapperClassName="demo-wrapper"
      editorClassName={`demo-editor form-control fw ${invalidState ? 'is-invalid' : ''}`}
      onEditorStateChange={onEditorStateChange}
      toolbar={{
        options,
        inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
        list: { options: ['unordered', 'ordered'] },
        link: {
          popupClassName: 'h-min-content',
          linkCallback: params => ({ ...params })
        },
        embedded: {
          popupClassName: 'h-min-content',
          embedCallback: linkEmbed => {
            if (!REGEX_HTTPS.test(linkEmbed)) linkEmbed = `https://${linkEmbed}`;

            const detectedSrc = /<iframe.*? src="(.*?)"/.exec(embed(linkEmbed));
            return (detectedSrc && detectedSrc[1]) || linkEmbed;
          }
        },
        image: {
          uploadCallback: uploadCallback,
          previewImage: true
        }
      }}
      toolbarCustomButtons={enableCustomColorPicker ? [<CustomColorOption key={1} />] : []}
    />
  );
};

export default RichEditor;
